/**
 * ImpactAreaCrossProgramChord.jsx
 *
 * D3 chord diagram showing cross-program collaboration for a specific
 * Impact Area. Each ribbon connects a home program to a contributing
 * initiative that shares at least one result tagged for this IA.
 */

import { useState, useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { chord as d3Chord, ribbon as d3Ribbon } from 'd3-chord';
import { arc as d3Arc } from 'd3-shape';
import { descending } from 'd3-array';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';

/* ---------- layout constants ---------- */
const SIZE = 520;
const CX = SIZE / 2;
const CY = SIZE / 2;
const OUTER_R = 220;
const INNER_R = 200;
const LABEL_R = OUTER_R + 14;

/**
 * Extract a short program code (e.g. "SP03") from a contributingInitiatives
 * entry like "SP03 - Sustainable Animal...".
 */
function extractCode(entry) {
  if (!entry || typeof entry !== 'string') return null;
  const trimmed = entry.trim();
  const match = trimmed.match(/^(SP\d{2}|INIT-\d{2}|SGP-\d{2})/i);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Parse the contributingInitiatives field which may be:
 * - An array (pipeline-processed)
 * - A string delimited by " ; "
 */
function parseContributing(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    return raw.split(/\s*;\s*/).filter(Boolean);
  }
  return [];
}

/**
 * Lighten a hex colour toward white.
 * amount 0 = original, 1 = white.
 */
function lighten(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.round(((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * amount);
  const g = Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount);
  const b = Math.round((num & 0xff) + (255 - (num & 0xff)) * amount);
  return `rgb(${r},${g},${b})`;
}

/**
 * A set of visually distinct colours for program arcs.
 */
const PROGRAM_PALETTE = [
  '#2196F3', '#FF9800', '#4CAF50', '#9C27B0', '#009688',
  '#F44336', '#3F51B5', '#FF5722', '#795548', '#607D8B',
  '#E91E63', '#00BCD4', '#8BC34A', '#FFC107', '#673AB7',
  '#CDDC39', '#03A9F4', '#FF6F00', '#1B5E20', '#AD1457',
];

function buildPalette(brandColor, count) {
  if (count <= 0) return [];
  if (count === 1) return [brandColor];
  return PROGRAM_PALETTE.slice(0, count);
}

const ImpactAreaCrossProgramChord = memo(function ImpactAreaCrossProgramChord({ results, color }) {
  const [tip, setTip] = useState(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  /* ---------- build adjacency matrix ---------- */
  const chordData = useMemo(() => {
    if (!results || results.length === 0) return null;

    // Collect all program-to-program edges
    const edgeMap = {}; // "SP01|SP03" → count
    const programSet = new Set();

    for (const r of results) {
      const home = extractCode(r.initiative || r.Initiative || '');
      if (!home) continue;

      const contribs = parseContributing(
        r.contributingInitiatives || r['Contributing Initiatives'] || r.contributingPrograms
      );

      for (const entry of contribs) {
        const code = extractCode(entry);
        if (!code || code === home) continue; // skip self-links

        programSet.add(home);
        programSet.add(code);

        const key = [home, code].sort().join('|');
        edgeMap[key] = (edgeMap[key] || 0) + 1;
      }
    }

    const programs = Array.from(programSet).sort();
    if (programs.length < 2) return null;

    const indexMap = {};
    programs.forEach((p, i) => { indexMap[p] = i; });

    const n = programs.length;
    const matrix = Array.from({ length: n }, () => new Float64Array(n));

    for (const [key, count] of Object.entries(edgeMap)) {
      const [a, b] = key.split('|');
      const ai = indexMap[a];
      const bi = indexMap[b];
      if (ai !== undefined && bi !== undefined) {
        matrix[ai][bi] = count;
        matrix[bi][ai] = count;
      }
    }

    const layout = d3Chord()
      .padAngle(0.04)
      .sortSubgroups(descending)
      .sortChords(descending)(matrix);

    const palette = buildPalette(color, n);

    return { programs, layout, indexMap, palette };
  }, [results, color]);

  /* ---------- generators ---------- */
  const arcGen = useMemo(() => d3Arc().innerRadius(INNER_R).outerRadius(OUTER_R), []);
  const ribbonGen = useMemo(() => d3Ribbon().radius(INNER_R - 2), []);

  /* ---------- hover helpers ---------- */
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredChord, setHoveredChord] = useState(null);

  const isArcHighlighted = useCallback((idx) => {
    if (hoveredChord !== null) {
      return idx === hoveredChord.source.index || idx === hoveredChord.target.index;
    }
    if (hoveredNode !== null) return idx === hoveredNode;
    return true;
  }, [hoveredNode, hoveredChord]);

  const isRibbonHighlighted = useCallback((ch) => {
    if (hoveredChord !== null) {
      return ch.source.index === hoveredChord.source.index &&
             ch.target.index === hoveredChord.target.index;
    }
    if (hoveredNode !== null) {
      return ch.source.index === hoveredNode || ch.target.index === hoveredNode;
    }
    return true;
  }, [hoveredNode, hoveredChord]);

  /* ---------- render ---------- */
  if (!chordData) {
    return (
      <ChartWrapper title="Cross-Program Collaboration">
        <NoChartData
          message="No cross-program collaboration data"
          detail="This chord diagram shows how programs collaborate on results for this Impact Area via Contributing Initiatives. No cross-program links were found in the current selection."
        />
      </ChartWrapper>
    );
  }

  const { programs, layout, palette } = chordData;
  const { groups } = layout;
  const chordArray = Array.from(layout);

  const exportData = chordArray.map(ch => ({
    Source: programs[ch.source.index],
    Target: programs[ch.target.index],
    'Shared Results': ch.source.value,
  }));

  return (
    <ChartWrapper
      title="Cross-Program Collaboration"
      info="Chord diagram showing how programs co-contribute to results for this Impact Area. Thicker ribbons indicate more shared results via Contributing Initiatives."
      chartData={exportData}
      exportFilename="ia-cross-program-chord"
    >
      <div className="flex justify-center relative">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="w-full"
          style={{ maxWidth: SIZE, height: 'auto' }}
        >
          <defs>
            {chordArray.map((ch, i) => {
              const srcColor = palette[ch.source.index] || color;
              const tgtColor = palette[ch.target.index] || color;
              const srcAngle = (ch.source.startAngle + ch.source.endAngle) / 2 - Math.PI / 2;
              const tgtAngle = (ch.target.startAngle + ch.target.endAngle) / 2 - Math.PI / 2;
              return (
                <linearGradient
                  key={i}
                  id={`ia-chord-grad-${i}`}
                  gradientUnits="userSpaceOnUse"
                  x1={CX + INNER_R * 0.5 * Math.cos(srcAngle)}
                  y1={CY + INNER_R * 0.5 * Math.sin(srcAngle)}
                  x2={CX + INNER_R * 0.5 * Math.cos(tgtAngle)}
                  y2={CY + INNER_R * 0.5 * Math.sin(tgtAngle)}
                >
                  <stop offset="0%" stopColor={srcColor} />
                  <stop offset="100%" stopColor={tgtColor} />
                </linearGradient>
              );
            })}
          </defs>

          <g transform={`translate(${CX},${CY})`}>
            {/* Ribbons */}
            {chordArray.map((ch, i) => {
              const highlighted = isRibbonHighlighted(ch);
              return (
                <path
                  key={`ribbon-${i}`}
                  d={ribbonGen(ch)}
                  fill={`url(#ia-chord-grad-${i})`}
                  opacity={highlighted ? 0.55 : 0.06}
                  style={{ transition: 'opacity 0.3s ease', cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    setHoveredChord(ch);
                    setTip({
                      source: programs[ch.source.index],
                      target: programs[ch.target.index],
                      count: ch.source.value,
                    });
                    setTipPos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => { setHoveredChord(null); setTip(null); }}
                />
              );
            })}

            {/* Arcs */}
            {groups.map((group, i) => {
              const highlighted = isArcHighlighted(i);
              const arcColor = palette[i] || color;
              const midAngle = (group.startAngle + group.endAngle) / 2;
              const lx = LABEL_R * Math.sin(midAngle);
              const ly = -LABEL_R * Math.cos(midAngle);
              const rotation = (midAngle * 180) / Math.PI;
              const flip = midAngle > Math.PI;
              return (
                <g key={`arc-${i}`}>
                  <path
                    d={arcGen(group)}
                    fill={arcColor}
                    opacity={highlighted ? 1 : 0.2}
                    stroke="#fff"
                    strokeWidth={1}
                    style={{ transition: 'opacity 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      setHoveredNode(i);
                      setHoveredChord(null);
                      setTip({ name: programs[i], color: arcColor, total: group.value });
                      setTipPos({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => { setHoveredNode(null); setTip(null); }}
                  />
                  <text
                    x={lx}
                    y={ly}
                    dy="0.35em"
                    textAnchor={flip ? 'end' : 'start'}
                    transform={`rotate(${rotation - 90},${lx},${ly})${flip ? ` rotate(180,${lx},${ly})` : ''}`}
                    fontSize={10}
                    fontWeight={700}
                    fill={highlighted ? '#1f2937' : '#d1d5db'}
                    style={{ transition: 'fill 0.3s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      setHoveredNode(i);
                      setHoveredChord(null);
                      setTip({ name: programs[i], color: arcColor, total: group.value });
                      setTipPos({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => { setHoveredNode(null); setTip(null); }}
                  >
                    {programs[i]}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>

        {/* Tooltip */}
        {tip && (
          <div
            className="fixed z-50 px-3 py-2 bg-white text-gray-800 text-xs rounded-lg shadow-lg border border-gray-200 pointer-events-none"
            style={{ left: tipPos.x + 12, top: tipPos.y - 28 }}
          >
            {tip.count !== undefined ? (
              <>
                <div className="font-semibold mb-1">{tip.source} &#8596; {tip.target}</div>
                <div>Shared results: <strong>{tip.count.toLocaleString()}</strong></div>
              </>
            ) : (
              <>
                <div className="font-semibold mb-1">{tip.name}</div>
                <div>Total shared results: <strong>{tip.total?.toLocaleString()}</strong></div>
              </>
            )}
          </div>
        )}
      </div>

    </ChartWrapper>
  );
});

ImpactAreaCrossProgramChord.propTypes = {
  results: PropTypes.array.isRequired,
  color: PropTypes.string.isRequired,
};

export default ImpactAreaCrossProgramChord;
